import { useState } from 'react';
import { FileCode2, Image as ImageIcon, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FileChange,
  HistoryFile,
} from '@/redux/postActivity/postActivity.types';
import TextSplitDiff from './TextSplitDiff';

/** Larger files compare by size and checksum only; a line diff would freeze the tab. */
const MAX_TEXT_DIFF_BYTES = 3 * 1024 * 1024;

const CATEGORY_LABEL: Record<string, string> = {
  postMedia: 'Images',
  postThumbnail: 'Thumbnail',
  postModel: 'G-code / model files',
};

const KIND_LABEL: Record<FileChange['kind'], string> = {
  added: 'Added',
  removed: 'Removed',
  replaced: 'Replaced',
};

const sizeOf = (bytes?: number) =>
  bytes === undefined ? '' : `${(bytes / 1024).toFixed(1)} KB`;

function FileLine({ file }: { file: HistoryFile }) {
  const Icon = file.resourceType === 'youtube' ? Youtube : FileCode2;
  return (
    <div className="flex min-w-0 items-center gap-2 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="truncate" title={file.fileName}>
        {file.fileName}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {sizeOf(file.fileSize)}
      </span>
    </div>
  );
}

const isShownAsImage = (file: HistoryFile) =>
  file.resourceType === 'image' && Boolean(file.url);

function FilePreview({ file }: { file?: HistoryFile }) {
  if (!file) {
    return <p className="text-xs italic text-muted-foreground">none</p>;
  }
  if (!isShownAsImage(file)) return <FileLine file={file} />;
  return (
    <img
      src={file.url ?? undefined}
      alt={file.fileName}
      className="max-h-48 w-full rounded-md border bg-neutral-50 object-contain"
    />
  );
}

const urlOf = (file?: HistoryFile) => file?.url ?? undefined;

const sizeOf0 = (file?: HistoryFile) => file?.fileSize ?? 0;

const withinCap = (file?: HistoryFile) =>
  Boolean(urlOf(file)) && sizeOf0(file) <= MAX_TEXT_DIFF_BYTES;

const isModelReplacement = (change: FileChange) =>
  change.kind === 'replaced' && change.before?.resourceType === 'model';

const canCompareText = (change: FileChange) =>
  isModelReplacement(change) &&
  withinCap(change.before) &&
  withinCap(change.after);

const fetchText = async (url = '') => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.text();
};

function CompareButton({
  state,
  onLoad,
}: {
  state: 'idle' | 'loading' | 'failed';
  onLoad: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <Button
        size="sm"
        variant="outline"
        onClick={onLoad}
        disabled={state === 'loading'}
      >
        {state === 'loading' ? 'Loading files…' : 'Compare file contents'}
      </Button>
      {state === 'failed' && (
        <span className="text-xs text-destructive-subtle">
          One of the files could not be loaded.
        </span>
      )}
    </div>
  );
}

function GcodeCompare({ before, after }: { before?: string; after?: string }) {
  const [texts, setTexts] = useState<[string, string] | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');

  const load = async () => {
    setState('loading');
    try {
      setTexts(await Promise.all([fetchText(before), fetchText(after)]));
    } catch {
      setState('failed');
    }
  };

  if (texts) return <TextSplitDiff before={texts[0]} after={texts[1]} mono />;
  return <CompareButton state={state} onLoad={load} />;
}

const partOf = (change: FileChange) =>
  change.after?.partId ?? change.before?.partId;

function FileChangeRow({
  change,
  labels,
}: {
  change: FileChange;
  labels: Record<string, string>;
}) {
  const partId = partOf(change);
  const part = partId ? ` · ${labels[partId] ?? 'Part'}` : '';
  return (
    <li className="rounded-lg border p-3">
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        {KIND_LABEL[change.kind]}
        {part}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0 rounded-md bg-red-50/60 p-2">
          <FilePreview file={change.before} />
        </div>
        <div className="min-w-0 rounded-md bg-green-50/60 p-2">
          <FilePreview file={change.after} />
        </div>
      </div>
      {canCompareText(change) && (
        <div className="mt-3">
          <GcodeCompare
            before={urlOf(change.before)}
            after={urlOf(change.after)}
          />
        </div>
      )}
    </li>
  );
}

const changeKey = (change: FileChange) =>
  [change.kind, change.before?.id, change.after?.id].join('-');

const byCategory = (changes: FileChange[]) => {
  const groups = new Map<string, FileChange[]>();
  changes.forEach((change) => {
    groups.set(change.category, [
      ...(groups.get(change.category) ?? []),
      change,
    ]);
  });
  return [...groups.entries()];
};

/** Images, thumbnail and G-code changes, before on the left and after on the right. */
export default function FileChanges({
  changes,
  labels,
}: {
  changes: FileChange[];
  labels: Record<string, string>;
}) {
  if (changes.length === 0) return null;
  return (
    <div className="flex flex-col gap-4">
      {byCategory(changes).map(([category, rows]) => (
        <section key={category}>
          <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            {CATEGORY_LABEL[category] ?? category}
          </h4>
          <ul className="flex flex-col gap-2">
            {rows.map((change) => (
              <FileChangeRow
                key={changeKey(change)}
                change={change}
                labels={labels}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
